/**
 * The Plant Brief's words (Foundry Intelligence, stage 1c; docs/plans/2026-09-26_foundry-intelligence.md). Every
 * sentence is built from the brief's own numbers and verdicts, in foundry language, and says no more than the verdict
 * does: a mechanism the data does not show is "not seen here", never "ruled out", and nothing is called a cause. The
 * defects are named in the plant's own words (the brief's labels). A number the brief states as a fact is printed in
 * the fact's own words (factTextOf), so the page and the consultant's number check agree; without one, it is
 * formatted as the engine formats it. Pure; no em or en dashes (house style).
 */
import { formatDay } from "@/lib/plant-days";
import { lotDateWords, lotDatesNote } from "./lot-dates";
import { shownFor } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";
import type {
  BriefBin,
  BriefBlockReason,
  BriefChange,
  BriefDataAsk,
  BriefDefect,
  BriefDriver,
  BriefFact,
  BriefMechanism,
  BriefUntestable,
  BriefVerdict,
  BriefWindow,
  PlantBrief,
} from "@/lib/api/brief";

/**
 * The words for a number the brief states as a fact: the fact's own text, looked up by its key, so the page and the
 * number check agree; the fallback (the value, formatted as the engine formats it) where the brief has no such fact.
 */
export function factTextOf(facts: readonly BriefFact[] | undefined): (key: string, fallback: string) => string {
  const byKey = new Map((facts ?? []).map((f) => [f.key, f.text]));
  return (key, fallback) => byKey.get(key) ?? fallback;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A fraction as a percentage: one decimal, two below 0.1%. Half rounds up (the epsilon absorbs binary noise). */
export function pct(v: number): string {
  const p = v * 100;
  if (Math.abs(p) < 0.1 && p !== 0) {
    const r = Math.round(p * 100 + 1e-9) / 100;
    // What rounds to nothing reads as nothing does: "0.0%", never "0.00%" beside it (the 2026-09-28 walk, D3).
    if (r === 0) return "0.0%";
    return `${r.toFixed(2)}%`;
  }
  return `${(Math.round(p * 10 + 1e-9) / 10).toFixed(1)}%`;
}

/** A key in words, each capitalised as the plant books it: "cold_iron" is "Cold Iron" (the 2026-09-28 walk, R3). */
export function keyWords(key: string): string {
  return key
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

/** A count with thousands separators, the same in every browser. */
export function count(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

/** "2021", "2026 to date" as given; a plant month "2026-05" as "May 2026". */
export function periodLabel(period: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : period;
}

function span(w: BriefWindow): string {
  return `${formatDay(w.from)} to ${formatDay(w.to)}`;
}

/**
 * A label inside a sentence: its first letter lowered when it starts an ordinary word ("Residual magnesium" reads
 * "residual magnesium"), kept for a short word or an abbreviation ("CE", "Mg treatment"), and kept for a plant's
 * title-case name ("Cold Iron", "Dirt / Sand Inclusion"): one whose later words, before any bracket, hold an ordinary
 * word written capitalised. A station's bracket and an abbreviation ("Residual Mg") are no such words. The engine's
 * briefInline (brief_engine.go) is the same rule.
 */
/**
 * The inputs one way of recording covers, named together (the foundry playbook's recording): the first as it heads a
 * line, the rest as they read inside a sentence ("Pour speed and fill time").
 */
export function recordingTitle(labels: readonly string[]): string {
  const words = labels.map((l, i) => (i === 0 ? l : inline(l)));
  return words.length <= 1 ? words.join("") : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/**
 * The proper nouns a label may start with, matched exactly as written (brief/v19): kept so inside a sentence. An
 * initialism needs no entry ("AFS" has two capitals, "CE" and "Mg" are two letters), and the match is never by lower
 * case: cerium's "Ce" must never read as carbon equivalent's "CE". The engine's briefProperNouns (brief_engine.go)
 * holds the same list.
 */
export const PROPER_NOUNS: ReadonlySet<string> = new Set(["Brinell"]);

export function inline(label: string): string {
  const [first, ...rest] = label.split(" ");
  if (PROPER_NOUNS.has(first)) return label;
  if (!(first.length > 2 && /^[A-Z][a-z]+$/.test(first))) return label;
  // What a bracket adds (a station) and the machine named after " on the " or " on " (machineAt, brief/v18) are no
  // title words of the label.
  let head = ` ${rest.join(" ").split("(")[0]}`;
  const at = machineAt(head);
  if (at >= 0) head = head.slice(0, at);
  if (head.split(/\s+/).some((w) => /^[A-Z][a-z]{2,}$/.test(w))) return label;
  return label[0].toLowerCase() + label.slice(1);
}

/** A machine's name that takes no article (the server's briefMachineBare): one ending in a number ("Muller 2"), or led
 *  by a word in capitals, two letters or more ("DISA 1", "OES"). */
export function machineBare(name: string): boolean {
  const n = name.trim();
  if (n === "") return false;
  if (/[0-9]$/.test(n)) return true;
  const first = n.split(" ")[0];
  return /^[A-Z0-9-]+$/.test(first) && (first.match(/[A-Z]/g) ?? []).length >= 2;
}

/** Where a label's machine starts (the server's briefMachineAt): " on the " in any case, else the first " on " before a
 *  name that takes no article; -1 when the label names no machine. */
export function machineAt(s: string): number {
  const i = s.replace(/[A-Z]/g, (c) => c.toLowerCase()).indexOf(" on the ");
  if (i >= 0) return i;
  for (let from = 0; ; ) {
    const j = s.indexOf(" on ", from);
    if (j < 0) return -1;
    if (machineBare(s.slice(j + " on ".length))) return j;
    from = j + 1;
  }
}

/**
 * A condition's value as the engine's watch writes it (brief_engine.go, briefNumText): whole from 999.5 up, else three
 * significant places or so, trailing zeros dropped ("1385", "0.0512", "12.3").
 */
export function numText(v: number): string {
  if (!Number.isFinite(v)) return String(v);
  if (Math.abs(v) >= 999.5) return String(Math.round(v));
  if (v === 0) return "0";
  const decimals = Math.max(0, 2 - Math.floor(Math.log10(Math.abs(v))));
  const s = v.toFixed(decimals);
  return s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s;
}

/** A scrap rate in words: nothing cast is no rate, never 0%. */
export function rateText(v: number | null): string {
  return v === null ? "no castings" : pct(v);
}

export function headline(b: PlantBrief): string {
  if (b.scrap.rate === null) {
    return `No castings with a known outcome were booked from ${span(b.windows.lots)}.`;
  }
  const t = factTextOf(b.facts);
  // A lot's date is worded as the plant dates its lots (B1b T3): poured, inspected or booked at shipping.
  const w = lotDateWords(b.lot_dates);
  return `${t("scrap.rate", pct(b.scrap.rate))} of the castings ${w.dated} from ${span(b.windows.lots)} were ${w.scrapped}: ${t("scrap.pieces", count(b.scrap.scrap))} pieces.`;
}

/** How concentrated the scrap is: the brief's top defects together (never a card beyond them), and the largest. */
export function concentrationSentence(b: PlantBrief): string {
  const tops = b.defects.filter((d) => !d.beyond_top);
  if (tops.length === 0) return "";
  const together = tops.reduce((s, d) => s + d.share, 0);
  const top = tops[0];
  const share = factTextOf(b.facts)(`pareto.${top.defect}.share`, pct(top.share));
  // Ranked by money (B2), the first card is the costliest, not the largest.
  const which = b.scrap.ranked_by === "money" ? "the costliest" : "the largest";
  return `${tops.length} defects make ${pct(together)} of it; ${which}, ${top.label}, is ${share}.`;
}

/**
 * The defects the plant holds at or under its own scrap targets (brief/v6): they have no card, since the plant set the
 * rate it accepts for them, so the page says which they are and where they run. "" when there are none.
 */
export function underTargetSentence(b: PlantBrief): string {
  const held = b.under_target ?? [];
  if (held.length === 0) return "";
  const t = factTextOf(b.facts);
  const each = held.map(
    (u) => `${u.label} (${t(`pareto.${u.defect}.rate`, pct(u.rate))} of the castings, its target ${t(`pareto.${u.defect}.target`, pct(u.target))})`,
  );
  return `Held at or under the plant's own targets, so without a card: ${each.join("; ")}.`;
}

/** Words in a list: "A", "A and B", "A, B and C". */
function wordsList(items: readonly string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * The scrap booked outside the foundry's own process (brief/v19, cadence/v18), one sentence apart from the defects:
 * "Another 1,240 pieces (4.1% of the scrap) were booked as Lathe 7 and Stock count, outside the foundry's own process,
 * so no defect counts them." Each number is its fact's words. "" where the brief has none.
 */
export function outsideProcessSentence(
  outside: { words: readonly string[]; pieces: number; share: number } | undefined,
  facts?: readonly BriefFact[],
): string {
  if (!outside || outside.pieces <= 0 || outside.words.length === 0) return "";
  const t = factTextOf(facts ?? []);
  const pieces = t("outside.pieces", count(outside.pieces));
  const noun = outside.pieces === 1 ? "piece" : "pieces";
  const verb = outside.pieces === 1 ? "was" : "were";
  return `Another ${pieces} ${noun} (${t("outside.share", pct(outside.share))} of the scrap) ${verb} booked as ${wordsList(outside.words)}, outside the foundry's own process, so no defect counts them.`;
}

/** The two windows, named separately: the outcomes' and the process data's, which may be far shorter. */
export function windowsSentence(b: PlantBrief): string {
  const t = factTextOf(b.facts);
  const lots = b.windows.lots;
  const dated = lotDateWords(b.lot_dates).dated;
  // No lot held back is said so, never "0 held lots" (the 2026-10-24 sweep, item 8.5).
  const held =
    lots.held_excluded <= 0
      ? "No lots were held back."
      : `${t("lots.held", count(lots.held_excluded))} held ${lots.held_excluded === 1 ? "lot is" : "lots are"} left out of every figure.`;
  const outcomes = `Outcomes: ${t("lots.count", count(lots.lots))} casting lots ${dated} from ${span(lots)}. ${held}`;
  const p = b.windows.process;
  if (!p) {
    return `${outcomes} There is no process data yet: the brief shows where the scrap is, and the why needs the plant's process records.`;
  }
  const note = lotDatesNote(b.lot_dates);
  return `${outcomes} Process data: ${t("heats.count", count(p.heats))} heats from ${span(p)}. Only lots ${dated} on those days can be tested against the process.${note ? ` ${note}` : ""}`;
}

/** A trend's size a year, as the engine words it (brief/v18): "0.9 percentage points a year"; the sentence says which
 *  way ("rising by", "falling by"), never a sign or "points" alone (docs/design/2026-10-23_writing-standard.md). */
export function perYearText(v: number): string {
  return `${pct(Math.abs(v)).replace(/%$/, "")} percentage points a year`;
}

/** A correlation to two decimals, as the engine prints r: "-0.24", "0.05". */
export function rText(r: number): string {
  return r.toFixed(2);
}

/** What changed, between named periods on the plant clock; a figure the brief states is in the fact's words. */
export function changeSentence(c: BriefChange, labelOf: (defect: string) => string, facts?: readonly BriefFact[]): string {
  const t = factTextOf(facts);
  if (c.kind === "level") {
    const what = c.defect === null ? "The scrap rate" : `${labelOf(c.defect)} as a share of castings`;
    const [fromKey, toKey] = c.fact_keys;
    const from = fromKey ? t(fromKey, pct(c.from.rate)) : pct(c.from.rate);
    const to = toKey ? t(toKey, pct(c.to.rate)) : pct(c.to.rate);
    return `${what} was ${from} in ${periodLabel(c.from.period)} and ${to} in ${periodLabel(c.to.period)}.`;
  }
  const what = c.defect === null ? "The scrap rate" : `${labelOf(c.defect)}'s share of castings`;
  const fallback = perYearText(c.sen_slope_per_year);
  // The trend names its slope's fact first (fact_keys); a brief composed before trends named theirs kept the slope
  // under trend.<defect>.per_year.
  const slopeKey = c.fact_keys?.[0];
  const perYear = slopeKey
    ? t(slopeKey, fallback)
    : c.defect === null
      ? fallback
      : t(`trend.${c.defect}.per_year`, fallback);
  const way = c.sen_slope_per_year >= 0 ? "rising" : "falling";
  return `${what} has been ${way} by ${perYear} (the monthly trend).`;
}

const GRAIN_WORD: Record<BriefDriver["grain"], string> = { lot: "lots", heat: "heats", day: "plant days" };

/**
 * A driver's fact: the key the driver names for it (fact_keys). A brief composed before drivers named their facts
 * kept them as driver.<defect>.<hypothesis>.<family>.<condition>[.tracking].<name>, found by the rest of its key; where
 * one hypothesis tests the same condition in two families, the value settles which.
 */
export function driverFact(
  facts: readonly BriefFact[] | undefined,
  defect: string,
  d: BriefDriver,
  name: string,
  value: number,
  fallback: string,
): string {
  const key = d.fact_keys?.[name];
  if (key !== undefined) return factTextOf(facts)(key, fallback);
  const head = `driver.${defect}.${d.hypothesis_id}.`;
  const tails = [`.${d.condition.key}.${name}`, `.${d.condition.key}.tracking.${name}`];
  const hits = (facts ?? []).filter((f) => f.key.startsWith(head) && tails.some((tail) => f.key.endsWith(tail)));
  const hit = hits.find((f) => Math.abs(f.value - value) < 1e-9) ?? (hits.length === 1 ? hits[0] : undefined);
  return hit?.text ?? fallback;
}

function upperFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * A correlation's strength in words, by its size: under 0.1 "a very weak link", under 0.3 "a weak link", under 0.5 "a
 * moderate link", else "a strong link". Its direction is said by the sentence around it.
 */
export function linkStrength(r: number): string {
  const a = Math.abs(r);
  if (a < 0.1) return "a very weak link";
  if (a < 0.3) return "a weak link";
  if (a < 0.5) return "a moderate link";
  return "a strong link";
}

/** Whether a driver was set aside by its input's negative controls (the engine's briefControlsSetAsideWords). */
function controlsSetAside(d: BriefDriver): boolean {
  return /^set aside: its checks against defects it cannot cause/i.test((d.set_aside ?? "").trim());
}

/** One evidence item, in words that never say more than its verdict; its numbers in its facts' words. */
export function driverSentence(d: BriefDriver, defect: BriefDefect, facts?: readonly BriefFact[], prefs?: DisplayPrefs): string {
  const fact = (name: string, value: number, fallback: string) => driverFact(facts, defect.defect, d, name, value, fallback);
  const condition = inline(d.condition.label);
  const where = d.stratum_label ? ` in ${d.stratum_label}` : "";
  const support = `${fact("n", d.n, count(d.n))} ${GRAIN_WORD[d.grain]}${where}`;
  // Set aside by its input's negative controls (results/v9): the data do show the two moving together, so "do not show"
  // would contradict the caveat beside it. Say what the data show, and why it is no finding; never an effect. In plain
  // words (the 2026-10-24 sweep, item 8.1): the input also moves with defects it has no way to cause. Set aside for
  // another reason (a part mix, a trend, one month: the caveat beside it says which), it is no finding, said so.
  if (d.set_aside) {
    return controlsSetAside(d)
      ? `${upperFirst(condition)} moves with ${defect.label} here (${support}), but it also moves with defects it has no ` +
          "way to cause, so the link may come from how the data were recorded."
      : `${upperFirst(condition)} moves with ${defect.label} here (${support}), but it is not read as a finding.`;
  }
  if (d.verdict === "not_supported") {
    return `The data here do not show ${condition} moving ${defect.label} (${support}).`;
  }
  const e = d.effect;
  if (d.test === "window" && e.rate_in !== undefined && e.rate_out !== undefined) {
    const out = fact("rate_out", e.rate_out, pct(e.rate_out));
    const inside = fact("rate_in", e.rate_in, pct(e.rate_in));
    // A tail test compares the lots at or beyond the input's quartile with the rest: never "outside its window". A both
    // tail compares the two outer quarters with the middle half between its two cuts.
    let s: string;
    if (e.tail === "both" && e.cut_low !== undefined && e.cut_high !== undefined) {
      const low = fact("cut_low", e.cut_low, valueText(e.cut_low, d.condition.unit, prefs));
      const high = fact("cut_high", e.cut_high, valueText(e.cut_high, d.condition.unit, prefs));
      s =
        `Lots whose ${condition} was at or below ${low} or at or above ${high} (the lowest and highest quarters) ` +
        `ran ${out} ${defect.label}, against ${inside} between them (${support}).`;
    } else if ((e.tail === "low" || e.tail === "high") && e.cut !== undefined) {
      const low = e.tail === "low";
      s =
        `Lots whose ${condition} was at or ${low ? "below" : "above"} ` +
        `${fact("cut", e.cut, valueText(e.cut, d.condition.unit, prefs))} (the ${low ? "lowest" : "highest"} ` +
        `quarter) ran ${out} ${defect.label}, against ${inside} ${low ? "above" : "below"} it (${support}).`;
    } else {
      s = `Lots whose ${condition} sat outside its window ran ${out} ${defect.label}, against ${inside} inside it (${support}).`;
    }
    if (e.excess_pieces !== undefined) {
      const pieces = fact("excess_pieces", Math.round(e.excess_pieces), count(e.excess_pieces));
      const ci = e.excess_pieces_ci;
      const range = ci
        ? ` (${fact("excess_pieces.low", Math.round(ci[0]), count(ci[0]))} to ${fact("excess_pieces.high", Math.round(ci[1]), count(ci[1]))})`
        : "";
      s += ` That is about ${pieces} pieces${range} over the days the process data covers.`;
    }
    return s;
  }
  if (e.r !== undefined) {
    // The link's strength in words, never a bare r with its sign (the 2026-10-24 sweep, item 8.1: rule 3).
    const way = e.r < 0 ? "lower" : "higher";
    return `${defect.label} ran higher where ${condition} was ${way}, ${linkStrength(e.r)} (${support}).`;
  }
  return `${defect.label} differed with ${condition} (${support}).`;
}

/** A value with its unit after a space, when it has one. */
/**
 * A tracking condition's unit (brief/v19, the engine's briefShareUnit): its values are the share of the day its
 * parameter spent in its setpoint band, a fraction from 0 to 1.
 */
export const SHARE_UNIT = "fraction";

/** A share of the day as a percentage of it, as the engine writes it (briefValueText): 0.412 is "41.2%". */
export function shareOfDayText(v: number): string {
  return `${numText(v * 100)}%`;
}

function withUnit(text: string, unit: string): string {
  return unit ? `${text} ${unit}` : text;
}

/**
 * A stored value with its unit as the engine writes one, in the org's units: the fallback where the brief has no fact
 * for the number (the server writes a fact's words in the org's units). With no choice, or a choice that does not
 * convert the unit, exactly what it always read.
 */
function valueText(v: number, unit: string, prefs?: DisplayPrefs): string {
  if (unit === SHARE_UNIT) return shareOfDayText(v);
  const s = shownFor(unit, prefs);
  return withUnit(numText(s.convert(v)), s.unit);
}

/**
 * A window test's bins in words, lowest first, each edge and rate in its facts' words ("Sonic by treatment
 * temperature: 1370 °C to 1385 °C, 2.0%; ..."): where the rate rises, which is where a limit belongs. Null under two
 * bins.
 */
export function binsSentence(d: BriefDriver, defect: BriefDefect, facts?: readonly BriefFact[], prefs?: DisplayPrefs): string | null {
  const bins = d.effect.bins ?? [];
  if (bins.length < 2) return null;
  return `${defect.label} by ${inline(d.condition.label)}: ${binsWords(d, defect, bins, "bin.", facts, prefs)}.`;
}

/** Bins in words, lowest first: each edge and rate its fact's words under prefix (bin.<i>., range.bin.<i>.). */
function binsWords(
  d: BriefDriver,
  defect: BriefDefect,
  bins: readonly BriefBin[],
  prefix: string,
  facts?: readonly BriefFact[],
  prefs?: DisplayPrefs,
): string {
  const fact = (name: string, value: number, fallback: string) => driverFact(facts, defect.defect, d, name, value, fallback);
  const unit = d.condition.unit;
  return bins
    .map((b, i) => {
      const lo = fact(`${prefix}${i}.lo`, b.lo, valueText(b.lo, unit, prefs));
      const hi = fact(`${prefix}${i}.hi`, b.hi, valueText(b.hi, unit, prefs));
      const rate = b.rate === undefined ? "not pooled" : fact(`${prefix}${i}.rate`, b.rate, pct(b.rate));
      return `${lo} to ${hi}, ${rate}`;
    })
    .join("; ");
}

/**
 * The range a driver's input fifths suggest (brief/v5), as a suggestion: "The data suggest treatment temperature at or
 * above 1361 °C: 90 lots there ran 4.6% Cold Iron, against 14.3% in the worst fifth. A suggestion, not a limit: the
 * plant sets its window." Both ends: "between X and Y"; the top alone: "at or below Y". Fifths that suggest none: "The
 * data do not show a range of ... where ... runs clearly lower." Null without fifths. Each number its fact's words.
 */
export function rangeSentence(d: BriefDriver, defect: BriefDefect, facts?: readonly BriefFact[], prefs?: DisplayPrefs): string | null {
  const r = d.range;
  if (!r || r.bins.length === 0) return null;
  const condition = inline(d.condition.label);
  if (!r.suggested || r.rate === undefined || r.worst_rate === undefined || (r.lo === undefined && r.hi === undefined)) {
    return `The data do not show a range of ${condition} where ${defect.label} runs clearly lower.`;
  }
  const fact = (name: string, value: number, fallback: string) => driverFact(facts, defect.defect, d, name, value, fallback);
  const unit = d.condition.unit;
  const lo = r.lo === undefined ? "" : fact("range.lo", r.lo, valueText(r.lo, unit, prefs));
  const hi = r.hi === undefined ? "" : fact("range.hi", r.hi, valueText(r.hi, unit, prefs));
  const where = lo && hi ? `between ${lo} and ${hi}` : lo ? `at or above ${lo}` : `at or below ${hi}`;
  const units = r.n === undefined ? "" : `${fact("range.n", r.n, count(r.n))} ${GRAIN_WORD[d.grain]} there ran `;
  const rate = fact("range.rate", r.rate, pct(r.rate));
  const worst = fact("range.worst_rate", r.worst_rate, pct(r.worst_rate));
  const ran = units ? `${units}${rate}` : `the rate there ran ${rate}`;
  return (
    `The data suggest ${condition} ${where}: ${ran} ${defect.label}, against ${worst} in the worst fifth. ` +
    "A suggestion, not a limit: the plant sets its window."
  );
}

/** What a driver's tests count, in words: "lots", "heats" or "plant days". */
export function unitsWordOf(d: BriefDriver): string {
  return GRAIN_WORD[d.grain];
}

/**
 * Where a driver's range leads to its lots: the Plan tab of the correlations page at the pair of the result the range
 * was read from (its family, the driver's group where it names one, the result), which opens the pair's drilldown with
 * its points, a lot apiece. Plant-wide evidence names no group: the tab opens its first, the whole plant, or a
 * one-grade plant's only grade. A family instance ("F6[grade]") names its family. Null where the brief names no result
 * (before brief/v7).
 */
export function rangeLotsHref(d: BriefDriver): string | null {
  const r = d.range;
  if (!r?.result_id || !r.family) return null;
  const q = new URLSearchParams({ tab: "plan", family: r.family.replace(/\[.*$/, "") });
  if (d.stratum) q.set("stratum", d.stratum);
  q.set("result", r.result_id);
  return `/app/twin/correlations?${q.toString()}`;
}

/** One fifth of a driver's input as "How this was worked out" lists it, each number its fact's words. */
export interface RangeFifth {
  range: string;
  units: string;
  castings: string | null;
  /** Its defective castings, where the brief states them: the rate is these of the castings. */
  defective: string | null;
  rate: string;
  /** Among the fifths the range pools; the worst fifth, whose rate the range must be clear of. */
  pooled: boolean;
  worst: boolean;
}

/** How a driver's suggested range was worked out: the steps in words, and its fifths. */
export interface RangeWorkings {
  steps: string[];
  fifths: RangeFifth[];
}

/** A small count in words: "three groups". */
const SMALL = ["no", "one", "two", "three", "four", "five"];

/** The first fifth with the lowest pooled rate, as the analysis picks the fifth a run grows from; none without a rate. */
function lowestFifthOf(bins: readonly BriefBin[]): number | undefined {
  let lowest: number | undefined;
  bins.forEach((b, i) => {
    if (b.rate !== undefined && (lowest === undefined || b.rate < bins[lowest].rate!)) lowest = i;
  });
  return lowest;
}

/**
 * How the analysis worked out the range a driver's fifths suggest (analysis_results_range.go resultSuggest; brief/v7),
 * in words the process engineer can check against the table of fifths: the fifths (as many as the percentile cuts
 * leave where values tie) and the units split into them, the run grown from the lowest-rate fifth, the check that kept
 * it (the difference's interval at its level, the level held over every pair of fifths, on castings divided by phi),
 * the ends, and the run it came from. Each number is its fact's words. A range from a brief that predates the check
 * says so; fifths that suggest none say why. Null without fifths.
 */
export function rangeWorkingsOf(
  d: BriefDriver,
  defect: BriefDefect,
  facts?: readonly BriefFact[],
  run?: { doneAt?: string; lotsFrom?: string; lotsTo?: string },
  prefs?: DisplayPrefs,
): RangeWorkings | null {
  const r = d.range;
  if (!r || r.bins.length === 0) return null;
  const fact = (name: string, value: number, fallback: string) => driverFact(facts, defect.defect, d, name, value, fallback);
  const unit = d.condition.unit;
  const condition = inline(d.condition.label);
  const units = GRAIN_WORD[d.grain];
  const inRun = (i: number) => r.from_bin !== undefined && r.to_bin !== undefined && i >= r.from_bin && i <= r.to_bin;
  const fifths = r.bins.map((b, i) => ({
    range: `${fact(`range.bin.${i}.lo`, b.lo, valueText(b.lo, unit, prefs))} to ${fact(`range.bin.${i}.hi`, b.hi, valueText(b.hi, unit, prefs))}`,
    units: fact(`range.bin.${i}.n`, b.n, count(b.n)),
    castings: b.castings === undefined ? null : fact(`range.bin.${i}.castings`, b.castings, count(b.castings)),
    defective: b.defective === undefined ? null : fact(`range.bin.${i}.defective`, b.defective, count(b.defective)),
    rate: b.rate === undefined ? "not pooled" : fact(`range.bin.${i}.rate`, b.rate, pct(b.rate)),
    pooled: inRun(i),
    worst: r.worst_bin === i,
  }));
  // The units split into the fifths: their total, where the driver's own count is its headline row's.
  const total = r.bins.reduce((sum, b) => sum + b.n, 0);
  const split = `${fact("range.units", total, count(total))} ${units} that recorded ${condition}`;
  const groups = r.bins.length;
  const steps = [
    groups === 5
      ? `The analysis split the ${split} into five groups by its value, cut at its 20th, 40th, 60th and 80th percentiles (the fifths below), and pooled each fifth's castings into its ${defect.label} rate.`
      : `The analysis split the ${split} by its value at its 20th, 40th, 60th and 80th percentiles. Values tied at a cut leave ${SMALL[groups] ?? count(groups)} group${groups === 1 ? "" : "s"} rather than five (the fifths below). It pooled each fifth's castings into its ${defect.label} rate.`,
  ];
  const checked =
    r.suggested &&
    r.from_bin !== undefined &&
    r.to_bin !== undefined &&
    r.worst_bin !== undefined &&
    r.rate !== undefined &&
    r.worst_rate !== undefined &&
    r.difference_lo !== undefined &&
    r.difference_hi !== undefined &&
    r.level !== undefined;
  if (checked) {
    const from = fact("range.from_fifth", r.from_bin! + 1, String(r.from_bin! + 1));
    const to = fact("range.to_fifth", r.to_bin! + 1, String(r.to_bin! + 1));
    const worst = fact("range.worst_fifth", r.worst_bin! + 1, String(r.worst_bin! + 1));
    // What the pooled rate is of: its defective castings of its castings, so it can be checked.
    const of =
      r.castings === undefined
        ? ""
        : r.defective === undefined
          ? ` over ${fact("range.castings", r.castings, count(r.castings))} castings`
          : `, ${fact("range.defective", r.defective, count(r.defective))} of ${fact("range.castings", r.castings, count(r.castings))} castings`;
    const pooledRate = `${fact("range.rate", r.rate!, pct(r.rate!))}${of}`;
    // The run grows from the fifth of the lowest rate: its neighbours at or below the midpoint join it.
    const at = r.lowest_bin ?? lowestFifthOf(r.bins);
    const lowestRate = at === undefined ? undefined : r.bins[at]?.rate;
    if (at !== undefined && lowestRate !== undefined) {
      const lowest = fact("range.lowest_fifth", at + 1, String(at + 1));
      const rate = fact(`range.bin.${at}.rate`, lowestRate, pct(lowestRate));
      steps.push(
        r.from_bin === r.to_bin
          ? `Fifth ${lowest} ran the lowest rate, ${rate}, and neither neighbour ran at or below the midpoint between the lowest and the highest rate, so it is pooled alone: ${pooledRate}.`
          : `Fifth ${lowest} ran the lowest rate, ${rate}. Its neighbours at or below the midpoint between the lowest and the highest rate join it, so fifths ${from} to ${to} are pooled: ${pooledRate}.`,
      );
    } else {
      steps.push(
        `Fifths ${from} to ${to}, the lowest-rate fifth with its neighbours at or below the midpoint between the lowest and the highest rate, are pooled: ${pooledRate}.`,
      );
    }
    const family =
      r.family_level === undefined
        ? " That level is set for every pair of fifths the check could have picked, since the best and the worst were chosen after looking."
        : ` That level holds ${fact("range.family_level", r.family_level, pct(r.family_level))} over every pair of fifths the check could have picked, since the best and the worst were chosen after looking.`;
    const phi =
      r.phi === undefined
        ? ""
        : ` Each side's castings were divided by phi, ${fact("range.phi", r.phi, numText(r.phi))}, the scatter between ${units} beyond chance${r.phi_source ? ` (${r.phi_source})` : ""}, so a few odd ${units} cannot fake certainty.`;
    steps.push(
      `The worst fifth, fifth ${worst}, ran ${fact("range.worst_rate", r.worst_rate!, pct(r.worst_rate!))}. Its rate minus the pooled rate is between ${fact("range.difference.low", r.difference_lo!, pct(r.difference_lo!))} and ${fact("range.difference.high", r.difference_hi!, pct(r.difference_hi!))} at ${fact("range.level", r.level!, pct(r.level!))} confidence, clear of zero, so the pooled fifths are kept.${family}${phi}`,
    );
    const lo = r.lo === undefined ? "" : fact("range.lo", r.lo, valueText(r.lo, unit, prefs));
    const hi = r.hi === undefined ? "" : fact("range.hi", r.hi, valueText(r.hi, unit, prefs));
    const where = lo && hi ? `from ${lo} to ${hi}` : lo ? `from ${lo} up, open above` : `up to ${hi}, open below`;
    steps.push(
      `The range runs ${where}: the pooled fifths' outer edges. An end is open where the pooled fifths reach the lowest or the highest value the data hold.`,
    );
    steps.push(
      "It is not a standard-deviation rule: it finds where the plant's own rate ran lowest. An association, not a cause: a suggestion for the process engineer, never a limit.",
    );
  } else if (r.suggested) {
    steps.push("This brief was composed before briefs carried the check that kept the range: the brief composed again states it.");
  } else {
    steps.push(`No range is suggested: ${r.why ? r.why.replace(/\.$/, "") : "the data do not show a better range"}.`);
  }
  if (run?.doneAt) {
    const on = formatDay(run.doneAt.slice(0, 10));
    const lots = run.lotsFrom && run.lotsTo ? `, over the lots booked from ${formatDay(run.lotsFrom)} to ${formatDay(run.lotsTo)}` : "";
    steps.push(`From the analysis run finished on ${on}${lots}.`);
  }
  return { steps, fifths };
}

/** The fifths a driver's range comes from, lowest first ("Sonic by treatment temperature, in fifths: ..."). Null without. */
export function rangeBinsSentence(d: BriefDriver, defect: BriefDefect, facts?: readonly BriefFact[], prefs?: DisplayPrefs): string | null {
  const bins = d.range?.bins ?? [];
  if (bins.length < 2) return null;
  return `${defect.label} by ${inline(d.condition.label)}, in fifths: ${binsWords(d, defect, bins, "range.bin.", facts, prefs)}.`;
}

/** What the reader must keep in mind about one evidence item. */
export function caveatsOf(d: BriefDriver): string[] {
  const out: string[] = [];
  // Set aside (results/v9): first, as it is why the verdict holds nothing. The negative controls' reason is the driver's
  // own sentence already, in plain words (8.1); any other reason is said here.
  if (d.set_aside && !controlsSetAside(d)) {
    const s = d.set_aside.trim().replace(/\.$/, "");
    out.push(`${s.charAt(0).toUpperCase()}${s.slice(1)}.`);
  }
  if (d.provenance.path === "plant_day") {
    // The 2026-10-24 sweep, item 8.1: how it was compared, in words, never "grain".
    out.push("Compared day by day, the readings of a day against the castings of that day.");
  } else if (d.flags.exploratory || (d.provenance.path === "lot_window" && !d.provenance.confirmed)) {
    out.push("Exploratory: the lag from the station to the pour is estimated, not confirmed by the plant.");
  }
  // Joined mould by mould (brief/v14): how, and what the check against the booked castings could and could not see.
  if (d.provenance.join?.note) out.push(d.provenance.join.note);
  if (d.flags.low_power) out.push("Few lots: read it as a lead, not a finding.");
  if (d.flags.sign_agrees === false) {
    out.push("The direction runs against the textbook mechanism: check how the condition is measured.");
  }
  if (d.grain === "lot") out.push("Compared lot by lot. It shows lots, not single castings.");
  return out;
}

const BLOCK_WORDS: Record<BriefBlockReason, string> = {
  no_input: "the plant does not record it yet",
  lag_unknown: "the lag between the station and the pour is not known",
  no_path: "the plant model has no connection from it to the pour",
  ambiguous_sample: "where the sample is taken is not known",
  too_few_lots: "too few lots carry it",
  no_contrast: "it never left its band or never varied here, so there is nothing to compare",
  withheld: "two instruments measure it and neither is the plant's reference",
};

/**
 * Why a check cannot be run yet. An input the plant does not record is said as the reason ("Cannot be tested yet,
 * because the plant does not record section size on each part yet."), so the missing input never reads as the thing
 * that cannot be tested (the 2026-10-24 sweep, item 8.3); any other reason after what it is about.
 */
export function untestableSentence(u: BriefUntestable): string {
  if (u.reason === "no_input" && u.missing.length > 0) {
    const items = u.missing.map(inline);
    const what = items.length === 1 ? items[0] : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;
    return `Cannot be tested yet, because the plant does not record ${what} yet.`;
  }
  return `Cannot be tested yet: ${u.missing.join(", ")} (${BLOCK_WORDS[u.reason]}).`;
}

export const VERDICT_LABEL: Record<BriefVerdict, string> = {
  supported: "The data supports it",
  suggestive: "A lead, not proven",
  not_supported: "Not seen in the data",
  untestable: "Cannot be tested yet",
};

/** A mechanism's standing: its strongest evidence, "untestable" with only blocked items, or not tested at all. */
export type MechanismVerdict = BriefVerdict | "not_tested";

export interface MechanismGroup {
  mechanism: BriefMechanism;
  verdict: MechanismVerdict;
  drivers: BriefDriver[];
  untestable: BriefUntestable[];
}

const RANK: Record<MechanismVerdict, number> = {
  supported: 0,
  suggestive: 1,
  not_supported: 2,
  untestable: 3,
  not_tested: 4,
};

const excess = (d: BriefDriver) => d.effect.excess_pieces ?? 0;

/** The evidence grouped under each playbook mechanism, as a consultant presents it: the strongest first. */
export function mechanismsOf(defect: BriefDefect): MechanismGroup[] {
  const groups = defect.mechanisms.map((mechanism, order) => {
    const drivers = defect.drivers
      .filter((d) => d.mechanism_id === mechanism.id)
      .sort((a, b) => RANK[a.verdict] - RANK[b.verdict] || excess(b) - excess(a));
    const untestable = defect.untestable.filter((u) => u.mechanism_id === mechanism.id);
    const verdict: MechanismVerdict =
      drivers.length > 0 ? drivers[0].verdict : untestable.length > 0 ? "untestable" : "not_tested";
    return { group: { mechanism, verdict, drivers, untestable }, order };
  });
  groups.sort(
    (a, b) =>
      RANK[a.group.verdict] - RANK[b.group.verdict] ||
      Math.max(0, ...b.group.drivers.map(excess)) - Math.max(0, ...a.group.drivers.map(excess)) ||
      a.order - b.order,
  );
  return groups.map((g) => g.group);
}

/**
 * A data ask: what it would let the plant test, in the plant's words, and the pieces they scrapped over the brief's
 * lots (the fact ask.<n>.pieces, n its place in the list from 1): "Record pour temperature: would let the plant test
 * Cold lap and Slag, which together scrapped 1,450 pieces from 3 Mar 2031 to 30 Sep 2031." (the 2026-10-24 sweep,
 * item 8.4: a sentence, with its "and", and no "lots window").
 */
export function dataAskSentence(
  a: BriefDataAsk,
  labelOf: (defect: string) => string,
  facts?: readonly BriefFact[],
  index?: number,
  lots?: BriefWindow,
): string {
  const names = a.unlocks.map(labelOf);
  const what = names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  const pieces = count(a.pieces_at_stake);
  const stake = index === undefined ? pieces : factTextOf(facts)(`ask.${index + 1}.pieces`, pieces);
  const which = names.length > 1 ? "which together scrapped" : "which scrapped";
  const when = lots ? ` from ${span(lots)}` : " over the lots the brief reads";
  return `${a.ask}: would let the plant test ${what}, ${which} ${stake} pieces${when}.`;
}

/**
 * A shift's name in a defect's "by shift" cut (B2): "Shift 1" for a shift the plant keys by its number or letter, and
 * a calendar's own label ("Day shift") as it is, which already says shift.
 */
export function shiftCutName(key: string): string {
  return /shift/i.test(key) ? key : `Shift ${key}`;
}
